import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  OnInit,
  Optional,
  output,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  InputTextComponent,
  InputRadioGroupComponent,
  SelectBoxComponent,
} from '@pedix-workspace/angular-ui-form-reactive';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { AddressComponent } from '../../address/address.component';

import {
  Address,
  CartItem,
  Country,
  EstablishmentCheckout,
  LatLng,
  ShippingEstimate,
  ShippingOption,
  TypesOfShippings,
  CorreoArgentinoAgency,
  formatAgencyHours,
  getActiveShippingOptions,
  toCheckoutShippingType,
} from '@pedix-workspace/utils';
import { ParcelShippingResponse, ShippingCostResponse } from '@pedix-workspace/shared-delivery';
import {
  ApiV1_AvailableIntegration,
  ApiV1_Establishment,
  ApiV1_Subsidiary,
  sortSubsidiariesByDistance,
} from '@pedix-workspace/api-v1';
import { ShippingCostResponseComponent } from '../../shipping-cost-response/shipping-cost-response.component';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  finalize,
  from,
  map,
  of,
  switchMap,
} from 'rxjs';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { DeliveryService } from '@pedix-workspace/pedixapp-core-services';
import { GeolocationService } from '@pedix-workspace/angular-ui-map';
import { ShippingNotAllowedComponent } from '../../shipping-cost-response/shipping-not-allowed/shipping-not-allowed.component';
import { ShippingCalculationComponent } from '../../shipping-cost-response/shipping-calculation/shipping-calculation.component';
import { SubsidiarySelectedEvent } from '../../end-order.service';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { IconMapMarkerComponent } from '@pedix-workspace/angular-ui-icons';
import {
  getSubsidiaryGroupName,
  getSubsidiariesWithoutTemporaryClosure,
  EstablishmentOpenedStatusTagComponent,
} from '@pedix-workspace/pedixapp-shared-catalog';

@Component({
  selector: 'pxw-end-order-shipping-option-fields',
  templateUrl: './end-order-shipping-option-fields.component.html',
  styleUrls: ['./end-order-shipping-option-fields.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    InputTextComponent,
    FormsModule,
    InputRadioGroupComponent,
    AddressComponent,
    SelectBoxComponent,
    ShippingCostResponseComponent,
    ShippingNotAllowedComponent,
    ShippingCalculationComponent,
    ButtonComponent,
    IconMapMarkerComponent,
    EstablishmentOpenedStatusTagComponent,
    NgTemplateOutlet,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, NgForm]],
      useFactory: (ngForm: NgForm) => ngForm,
    },
  ],
})
export class EndOrderShippingOptionFieldsComponent implements OnInit {
  private readonly deliveryService = inject(DeliveryService);
  private readonly geolocationService = inject(GeolocationService);
  private readonly platformId = inject(PLATFORM_ID);

  readonly workerServicesUrl = input.required<string>();
  readonly countryCodes = input.required<Country[] | null>();
  readonly apiUrl = input.required<string>();
  readonly checkoutData = input.required<EstablishmentCheckout>();
  readonly establishment = input.required<ApiV1_Establishment>();
  readonly totalAmountWithDiscount = input.required<number>();
  readonly cartItems = input.required<CartItem[]>();
  readonly availableLogisticIntegrations = input.required<ApiV1_AvailableIntegration[]>();
  readonly name = input.required<string | null | undefined>();
  readonly phone = input.required<string | null | undefined>();

  readonly isFormSubmitted = input<boolean>(false);
  readonly previousShippingEstimate = input<ShippingEstimate | null>(null);
  readonly subsidiarySelector = input<boolean>(false);
  readonly subsidiaries = input<ApiV1_Subsidiary[]>([]);
  readonly selectedSubsidiaryId = input<string | null>();
  readonly restrictionHint = input<string | null>(null);

  readonly shippingOptionType = model.required<TypesOfShippings | null>();
  readonly address = model.required<Address | null>();
  readonly tableNumber = model.required<string | null>();
  readonly shippingCostResponse = model<ShippingCostResponse | null>(null);
  readonly calculatingShippingCost = model<boolean>(false);

  // NOTE: we want to delegate by event the subsidiary change, as it requires some asynchronous logic
  // but we want to instantly reflect changes in the selector; this is why we use a linked signal instead of model
  readonly selectedSubsidiaryIdLinked = linkedSignal(() => {
    const selectedSubsidiaryId = this.selectedSubsidiaryId();
    const deliveryMatches = this.matchingDeliverySubsidiaries();
    const shippingOptionType = this.shippingOptionType();

    if (shippingOptionType === TypesOfShippings.DELIVERY && deliveryMatches.length > 0) {
      return (
        deliveryMatches.find(m => m.establishmentId === selectedSubsidiaryId)?.establishmentId ||
        null
      );
    }

    return selectedSubsidiaryId;
  });

  readonly isFirstTimeCalculatingShippingCost = signal(true);
  readonly selectedParcelOption = signal<ShippingEstimate | null>(null);
  readonly selectedBranch = signal<CorreoArgentinoAgency | null>(null);
  readonly checkingShippingCoverage = signal(false);
  readonly shippingCoverageError = signal<'not-found' | 'unknown-error' | null>(null);
  readonly shippingEnabled = signal(false);
  readonly matchingDeliverySubsidiaries = signal<EstablishmentCheckout[]>([]);
  private readonly userCoordinates = signal<LatLng | null>(null);

  readonly selectedShippingOptionChange = output<ShippingOption | null>();
  readonly subsidiarySelected = output<SubsidiarySelectedEvent>();
  readonly openSubsidiariesSelector = output<void>();

  // NOTE: we merge together the DELIVERY and PARCEL types in the shippingOptionTypes selector
  readonly shippingOptionTypes = computed<TypesOfShippings[]>(() =>
    Array.from(
      new Set(
        getActiveShippingOptions(this.checkoutData()).map(option =>
          toCheckoutShippingType(option.type),
        ),
      ),
    ),
  );
  // NOTE: because we merged together the DELIVERY and PARCEL types in the shippingOption selector
  // we need a way to get the correct shippingOption, which in this case will be received as part of the shippingCostResponse
  // otherwhise, get the shippingOption from the checkoutData configuration
  readonly selectedShippingOption = computed(() => {
    if (!this.shippingOptionType()) {
      return null;
    }
    if (this.shippingOptionType() === TypesOfShippings.IN_PLACE) {
      return this.checkoutData().shippingOptionInPlace!;
    }
    if (this.shippingOptionType() === TypesOfShippings.TAKE_AWAY) {
      return this.checkoutData().shippingOptionTakeAway!;
    }
    return (
      this.shippingCostResponse()?.shippingOption ||
      getActiveShippingOptions(this.checkoutData()).find(
        option => toCheckoutShippingType(option.type) === TypesOfShippings.DELIVERY,
      ) ||
      null
    );
  });
  readonly selectedSubsidiaryAddress = computed(() => {
    const selectedSubsidiaryId = this.selectedSubsidiaryIdLinked();

    if (!selectedSubsidiaryId) {
      return '';
    }
    const subsidiary = this.subsidiaries().find(
      subsidiary => subsidiary.id === selectedSubsidiaryId,
    );

    return subsidiary?.address || subsidiary?.addressDetails?.fullAddress || '';
  });
  readonly subsidiarySelectorEnabled = computed(
    () =>
      this.subsidiarySelector() &&
      (this.selectedShippingOption()?.type === TypesOfShippings.IN_PLACE ||
        this.selectedShippingOption()?.type === TypesOfShippings.TAKE_AWAY),
  );
  readonly selectedGroupSubsidiaries = computed(() => {
    const currentSubsidiaryGroupName = getSubsidiaryGroupName(this.establishment());
    const userCoordinates = this.userCoordinates();

    const filtered = this.subsidiaries().filter(
      subsidiary => currentSubsidiaryGroupName === getSubsidiaryGroupName(subsidiary),
    );

    if (userCoordinates) {
      return sortSubsidiariesByDistance(filtered, userCoordinates);
    }

    return filtered;
  });
  readonly showDeliverySubsidiarySelector = computed(
    () => this.matchingDeliverySubsidiaries().length > 1,
  );
  readonly deliverySubsidiaryOptions = computed(() => {
    const matches = this.matchingDeliverySubsidiaries();
    const subsidiariesMap = new Map(this.subsidiaries().map(sub => [sub.id, sub]));

    return matches.map(match => {
      const subsidiary = subsidiariesMap.get(match.establishmentId);

      return {
        id: match.establishmentId,
        name: subsidiary?.name || match.establishmentId,
        address: subsidiary?.address || subsidiary?.addressDetails?.fullAddress || '',
        workingDays: subsidiary?.workingDays,
        temporaryClosure: subsidiary?.temporaryClosure,
        // NOTE: needed to get the timezone of the subsidiary
        addressDetails: subsidiary?.addressDetails,
        countryCode: subsidiary?.countryCode,
      };
    });
  });

  protected typesOfShippings = TypesOfShippings;

  async ngOnInit() {
    if (
      isPlatformBrowser(this.platformId) &&
      (await this.geolocationService.isPermissionGranted())
    ) {
      const result = await this.geolocationService.geolocate();

      this.userCoordinates.set(result);
    }
  }

  constructor() {
    effect(() => {
      this.selectedShippingOptionChange.emit(this.selectedShippingOption());
    });
    effect(() => {
      const shippingOptionType = this.shippingOptionType();
      const shippingOptionTypes = this.shippingOptionTypes();

      if (shippingOptionType && !shippingOptionTypes.includes(shippingOptionType)) {
        this.shippingOptionType.set(null);
      }
    });

    // When subsidiarySelector is enabled and selected shippingOptionType is DELIVERY
    // we need to check if address is within coverage of any available subsidiary
    // If it is and the subsidiary is not the current establishment, we need to emit subsidiarySelected event
    // While validating the subsidiary coverage, we need to disable further shipping handling (using shippingEnabled false)
    combineLatest({
      subsidiarySelector: toObservable(this.subsidiarySelector).pipe(distinctUntilChanged()),
      selectedSubsidiaryId: toObservable(this.selectedSubsidiaryId).pipe(distinctUntilChanged()),
      shippingOptionType: toObservable(this.shippingOptionType).pipe(distinctUntilChanged()),
      coordinates: toObservable(this.address).pipe(
        map(address => address?.addressDetails?.coordinates || null),
        distinctUntilChanged(),
      ),
    })
      .pipe(
        // IMPORTANT: non-DELIVERY emissions must reach switchMap so it cancels any in-flight delivery API call
        switchMap(
          ({ shippingOptionType, subsidiarySelector, coordinates, selectedSubsidiaryId }) => {
            if (shippingOptionType !== TypesOfShippings.DELIVERY) {
              return EMPTY;
            }

            if (subsidiarySelector) {
              this.shippingEnabled.set(false);
              this.shippingCoverageError.set(null);
              this.matchingDeliverySubsidiaries.set([]);
            } else {
              this.shippingEnabled.set(true);
            }

            if (!subsidiarySelector || !coordinates) {
              return EMPTY;
            }

            const establishmentsIds = getSubsidiariesWithoutTemporaryClosure(
              this.subsidiaries(),
            ).map(subsidiary => subsidiary.id);

            if (establishmentsIds.length === 0) {
              return of({ response: 'not-found' as const, selectedSubsidiaryId });
            }

            this.checkingShippingCoverage.set(true);

            return from(
              this.deliveryService
                .getMatchingSubsidiaryForAddress({
                  apiBaseUrl: this.apiUrl(),
                  coordinates,
                  establishmentsIds,
                })
                .catch(() => null),
            ).pipe(
              map(response => ({ response, selectedSubsidiaryId })),
              // finalize runs on both completion AND switchMap cancellation,
              // ensuring checkingShippingCoverage is always reset
              finalize(() => this.checkingShippingCoverage.set(false)),
            );
          },
        ),
        takeUntilDestroyed(),
      )
      .subscribe(({ response, selectedSubsidiaryId }) => {
        if (response === null) {
          this.shippingCoverageError.set('unknown-error');
        } else if (response === 'not-found') {
          this.shippingCoverageError.set('not-found');
        } else {
          const currentIsMatch = response.matches.some(
            m => m.establishmentId === selectedSubsidiaryId,
          );

          if (currentIsMatch) {
            this.shippingEnabled.set(true);
            if (response.matches.length > 1) {
              this.matchingDeliverySubsidiaries.set(response.matches);
            }
          } else if (response.matches.length === 1) {
            this.subsidiarySelected.emit({
              selectedSubsidiaryId: response.matches[0].establishmentId,
              establishmentCheckout: response.matches[0],
            });
          } else if (response.matches.length > 1) {
            this.matchingDeliverySubsidiaries.set(response.matches);
          }
        }
      });
  }

  onManuallySelectedSubsidiary(selectedSubsidiaryId: string) {
    this.subsidiarySelected.emit({
      selectedSubsidiaryId,
    });
  }

  onDeliverySubsidiarySelected(selectedSubsidiaryId: string) {
    const match = this.matchingDeliverySubsidiaries().find(
      m => m.establishmentId === selectedSubsidiaryId,
    );

    if (match) {
      this.subsidiarySelected.emit({
        selectedSubsidiaryId,
        establishmentCheckout: match,
      });
    }
  }

  onParcelOptionSelected(option: ShippingEstimate | null) {
    this.selectedParcelOption.set(option);

    const currentResponse = this.shippingCostResponse();

    if (!currentResponse) {
      return;
    }

    const isSelectionRequired = currentResponse.type === 'parcel-shipping-selection-required';
    const isParcelShipping = currentResponse.type === 'parcel-shipping';

    if (!isSelectionRequired && !isParcelShipping) {
      return;
    }

    if (option) {
      const minimumForFree =
        'minimumPurchaseForFreeShipping' in currentResponse
          ? currentResponse.minimumPurchaseForFreeShipping
          : undefined;

      const isFree = minimumForFree ? this.totalAmountWithDiscount() >= minimumForFree : false;

      const availableOptions =
        'availableParcelOptions' in currentResponse ? currentResponse.availableParcelOptions : [];

      const updatedResponse: ParcelShippingResponse = {
        ...currentResponse,
        type: 'parcel-shipping',
        outOfDeliveryZone: false,
        deliveryCost: isFree ? 0 : option.price,
        shippingEstimate: option,
        availableParcelOptions: availableOptions,
        requiresParcelSelection: true,
        ...(isFree && {
          freeShippingGrantedAmount: minimumForFree,
        }),
      };

      this.shippingCostResponse.set(updatedResponse);
    } else if (isSelectionRequired) {
      const updatedResponse: ShippingCostResponse = {
        ...currentResponse,
        type: 'parcel-shipping-selection-required',
        requiresParcelSelection: true,
      };

      this.shippingCostResponse.set(updatedResponse);
    }
  }

  onBranchSelected(agency: CorreoArgentinoAgency | null) {
    this.selectedBranch.set(agency);

    const currentResponse = this.shippingCostResponse();

    if (!currentResponse || currentResponse.type !== 'parcel-shipping') {
      return;
    }

    if (!currentResponse.shippingEstimate) {
      return;
    }

    const agencyInfo =
      agency && agency.code && agency.location
        ? {
            id: agency.code,
            name: agency.name || '',
            address: [agency.location.address.streetName, agency.location.address.streetNumber]
              .filter(Boolean)
              .join(' '),
            city: agency.location.address.city || '',
            state: agency.location.address.province || '',
            schedule: formatAgencyHours(agency.hours) || undefined,
          }
        : null;

    const existingInfo = this.parseAdditionalInfo(currentResponse.shippingEstimate.additionalInfo);
    delete existingInfo['agencyInfo'];

    const newAdditionalInfo = agencyInfo ? { ...existingInfo, agencyInfo } : existingInfo;

    const updatedShippingEstimate: ShippingEstimate = {
      ...currentResponse.shippingEstimate,
      agency: agency?.code || undefined,
      additionalInfo:
        Object.keys(newAdditionalInfo).length > 0 ? JSON.stringify(newAdditionalInfo) : undefined,
    };

    const updatedResponse: ParcelShippingResponse = {
      ...currentResponse,
      shippingEstimate: updatedShippingEstimate,
    };

    this.shippingCostResponse.set(updatedResponse);
  }

  private parseAdditionalInfo(additionalInfo?: string): Record<string, unknown> {
    if (!additionalInfo) return {};
    try {
      return JSON.parse(additionalInfo);
    } catch {
      return {};
    }
  }
}
